/**
 * Where cached responses live: IndexedDB database `alkokh-cache`.
 *
 * ⚠️ A SEPARATE DATABASE FROM `alkokh-offline`, ON PURPOSE. That one holds unsent
 * sales and drafts — work that exists nowhere else — which is why the
 * "clear cached data" button refuses to touch it. This one is pure cache: every
 * row can be re-fetched, so that button should wipe it freely, and a corrupted
 * cache can be cleared without going anywhere near real work.
 *
 * Not `localStorage`: `docs/README.frontend-caching-plan.md` §11 bans response
 * payloads there, and it is synchronous, small, and would stall the till.
 *
 * ⚠️ EVERY FAILURE PATH RESOLVES TO null OR A NO-OP. A cache that cannot open, or
 * a transaction that aborts, must never break a screen — the caller simply goes to
 * the network, exactly as it did before this file existed.
 */

import type { CacheTier } from "./cachePolicy";

export const CACHE_DB_NAME = "alkokh-cache";
const CACHE_DB_VERSION = 1;
const STORE_ENTRIES = "entries";

/** Above this many entries, the least recently used are dropped. */
const MAX_ENTRIES = 2000;

/** Stop storing when the origin is this full: a cache must never crowd out the outbox. */
const STORAGE_PRESSURE_RATIO = 0.8;

export interface CacheEntry<T = unknown> {
  key: string;
  body: T;
  /** Epoch ms when the server answered. Shown to the user for the asOf tier. */
  cachedAt: number;
  tier: CacheTier;
  tags: string[];
  user: string;
  /** Epoch ms of the last read, for eviction. */
  lastUsedAt: number;
  /** Rough byte size, for the budget. */
  bytes: number;
}

let dbPromise: Promise<IDBDatabase | null> | null = null;

function factory(): IDBFactory | null {
  if (typeof window === "undefined")
    return null;

  try {
    return window.indexedDB ?? null;
  }
  catch {
    return null;
  }
}

function openDb(): Promise<IDBDatabase | null> {
  if (dbPromise)
    return dbPromise;

  const idb = factory();

  if (!idb)
    return Promise.resolve(null);

  dbPromise = new Promise<IDBDatabase | null>((resolve) => {
    let request: IDBOpenDBRequest;

    /**
     * ⚠️ AN OPEN THAT NEVER SETTLES MUST NOT HANG EVERY READ. Safari has a known failure mode
     * where `indexedDB.open` fires neither success nor error; every cached read (picker searches
     * included) then awaited forever and showed nothing. Give up after 2s — `null` already means
     * "no cache, go to the network" — and clear `dbPromise` so the next read tries again.
     */
    let timedOut = false;

    const openTimeout = setTimeout(() => {
      timedOut = true;
      dbPromise = null;
      resolve(null);
    }, 2000);

    const settle = (db: IDBDatabase | null) => {
      clearTimeout(openTimeout);
      resolve(db);
    };

    try {
      request = idb.open(CACHE_DB_NAME, CACHE_DB_VERSION);
    }
    catch {
      settle(null);

      return;
    }

    request.onupgradeneeded = () => {
      const db = request.result;

      if (!db.objectStoreNames.contains(STORE_ENTRIES)) {
        const store = db.createObjectStore(STORE_ENTRIES, { keyPath: "key" });

        store.createIndex("user", "user", { unique: false });
        store.createIndex("lastUsedAt", "lastUsedAt", { unique: false });
        // Several tags per entry, so one write can clear everything a doctype touches.
        store.createIndex("tags", "tags", { unique: false, multiEntry: true });
      }
    };

    request.onsuccess = () => {
      const db = request.result;

      // Gave up already: a later read opens its own connection, so do not leak this one.
      if (timedOut) {
        db.close();

        return;
      }

      db.onversionchange = () => {
        db.close();
        dbPromise = null;
      };

      settle(db);
    };

    request.onerror = () => {
      dbPromise = null;
      settle(null);
    };

    request.onblocked = () => settle(null);
  });

  return dbPromise;
}

function tx<T>(mode: IDBTransactionMode, work: (store: IDBObjectStore) => IDBRequest<T>): Promise<T | null> {
  return openDb().then(db => new Promise<T | null>((resolve) => {
    if (!db) {
      resolve(null);

      return;
    }

    try {
      const transaction = db.transaction(STORE_ENTRIES, mode);
      const request = work(transaction.objectStore(STORE_ENTRIES));

      request.onsuccess = () => resolve((request.result as T) ?? null);
      request.onerror = () => resolve(null);
      transaction.onabort = () => resolve(null);
    }
    catch {
      resolve(null);
    }
  }));
}

export async function readEntry<T>(key: string): Promise<CacheEntry<T> | null> {
  const entry = await tx<CacheEntry<T>>("readonly", store => store.get(key) as IDBRequest<CacheEntry<T>>);

  if (!entry)
    return null;

  // Touch for LRU, deliberately not awaited: a read must not wait on bookkeeping.
  void tx("readwrite", store => store.put({ ...entry, lastUsedAt: Date.now() }));

  return entry;
}

export async function writeEntry<T>(entry: Omit<CacheEntry<T>, "lastUsedAt" | "bytes">): Promise<void> {
  let bytes = 0;

  try {
    bytes = JSON.stringify(entry.body)?.length ?? 0;
  }
  catch {
    // Not serialisable (a Blob, a circular structure). Refuse rather than guess.
    return;
  }

  if (await underStoragePressure())
    return;

  await tx("readwrite", store => store.put({ ...entry, lastUsedAt: Date.now(), bytes }));
  void evictIfNeeded();
}

export async function deleteEntry(key: string): Promise<void> {
  await tx("readwrite", store => store.delete(key));
}

/** Drop everything a mutation made stale. */
export async function deleteByTags(tags: string[]): Promise<number> {
  if (!tags.length)
    return 0;

  const db = await openDb();

  if (!db)
    return 0;

  return new Promise<number>((resolve) => {
    let removed = 0;

    try {
      const transaction = db.transaction(STORE_ENTRIES, "readwrite");
      const store = transaction.objectStore(STORE_ENTRIES);
      const index = store.index("tags");
      let pending = tags.length;

      const finish = () => {
        if (--pending <= 0)
          resolve(removed);
      };

      for (const tag of tags) {
        const request = index.openKeyCursor(IDBKeyRange.only(tag));

        request.onsuccess = () => {
          const cursor = request.result;

          if (cursor) {
            store.delete(cursor.primaryKey);
            removed += 1;
            cursor.continue();
          }
          else {
            finish();
          }
        };

        request.onerror = () => finish();
      }

      transaction.onabort = () => resolve(removed);
    }
    catch {
      resolve(removed);
    }
  });
}

/** Everything belonging to one user — the sign-out purge. */
export async function deleteByUser(user: string): Promise<void> {
  const db = await openDb();

  if (!db)
    return;

  await new Promise<void>((resolve) => {
    try {
      const transaction = db.transaction(STORE_ENTRIES, "readwrite");
      const store = transaction.objectStore(STORE_ENTRIES);
      const request = store.index("user").openKeyCursor(IDBKeyRange.only(user));

      request.onsuccess = () => {
        const cursor = request.result;

        if (cursor) {
          store.delete(cursor.primaryKey);
          cursor.continue();
        }
        else {
          resolve();
        }
      };

      request.onerror = () => resolve();
      transaction.onabort = () => resolve();
    }
    catch {
      resolve();
    }
  });
}

export async function clearAll(): Promise<void> {
  await tx("readwrite", store => store.clear());
}

export async function countEntries(): Promise<number> {
  return (await tx<number>("readonly", store => store.count())) ?? 0;
}

async function underStoragePressure(): Promise<boolean> {
  if (typeof navigator === "undefined" || !navigator.storage?.estimate)
    return false;

  try {
    const { usage = 0, quota = 0 } = await navigator.storage.estimate();

    return quota > 0 && usage / quota >= STORAGE_PRESSURE_RATIO;
  }
  catch {
    return false;
  }
}

/** Least recently used first, down to the cap. */
async function evictIfNeeded(): Promise<void> {
  const total = await countEntries();

  if (total <= MAX_ENTRIES)
    return;

  const db = await openDb();

  if (!db)
    return;

  const excess = total - MAX_ENTRIES;

  await new Promise<void>((resolve) => {
    try {
      const transaction = db.transaction(STORE_ENTRIES, "readwrite");
      const store = transaction.objectStore(STORE_ENTRIES);
      const request = store.index("lastUsedAt").openKeyCursor();
      let removed = 0;

      request.onsuccess = () => {
        const cursor = request.result;

        if (cursor && removed < excess) {
          store.delete(cursor.primaryKey);
          removed += 1;
          cursor.continue();
        }
        else {
          resolve();
        }
      };

      request.onerror = () => resolve();
      transaction.onabort = () => resolve();
    }
    catch {
      resolve();
    }
  });
}

/** Test seam. */
export function resetCacheDbHandle(): void {
  dbPromise = null;
}
